/**
 * @fileoverview Step 3: Find Flaws in County's Evidence Page
 * @module Step3FlawsPage
 * 
 * Business Context:
 * - Teaches users how to analyze and challenge county's valuation
 * - Intro videos explain how to read county evidence
 * - Each of the 4 flaws has video + interactive checklist pairing
 * - Persists user findings to backend for use in argument generation
 * 
 * Layout:
 * - LEFT: Course navigation (video list + flaw checklists)
 * - RIGHT: Video player
 * 
 * Content Structure:
 * 1. Intro videos: Understanding county valuation + reading evidence packets
 * 2. Per-flaw sections with video + interactive checklist
 * 3. Summary and next steps
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import VideoPlayer from '../components/course/VideoPlayer';
// Evidence packet gate is now handled on the dashboard before navigation to this page
import { 
  FlawSection, 
  FlawType, 
  FlawActivityType,
  TextboxConfig,
  TextboxFlawData,
  ClassificationResource,
  MisclassificationItem
} from '../components/flaws';
import {
  ArrowLeft,
  Search,
  ChevronRight,
  ChevronDown,
  Play,
  CheckCircle2,
  Clock,
  Loader2,
  AlertCircle,
  FileSearch,
  Info,
  BookOpen,
  ClipboardList
} from 'lucide-react';

interface VideoContent {
  id: string;
  title: string;
  description: string;
  duration: string;
  durationSeconds: number;
  videoUrl: string;
  category: 'intro' | 'flaw' | 'outro';
  flawType?: FlawType;
  flawNumber?: number;
  isDescriptionVideo?: boolean;
}

interface FlawDefinition {
  label: string;
  activityType: FlawActivityType;
  items: { key: string; label: string }[];
  textboxConfig?: TextboxConfig;
  resources?: ClassificationResource[];
}

interface FlawFinding {
  id: string;
  flawType: FlawType;
  // Can be boolean checklist data or textbox data - handled by FlawSection based on activityType
  checklistData: Record<string, boolean | string>;
  followUpData: Record<string, Record<string, string | number>> | null;
  notes: string | null;
}

// Flaw descriptions for UI
const FLAW_DESCRIPTIONS: Record<FlawType, { title: string; shortTitle: string; description: string; whyItMatters: string }> = {
  WRONG_DETAILS: {
    title: 'Property Details Are Wrong',
    shortTitle: 'Wrong Property Details',
    description: 'The county may have incorrect data about your property such as square footage, number of bedrooms/bathrooms, lot size, or year built.',
    whyItMatters: 'If the county has your property recorded with more square footage or features than you actually have, your valuation will be inflated.'
  },
  QUALITY_MISCLASSIFICATION: {
    title: 'Quality Misclassification',
    shortTitle: 'Quality Misclassification',
    description: 'Property appraisers assign a quality rating (like "Average", "Good", "Excellent") that affects your value. Being rated higher than justified inflates your taxes.',
    whyItMatters: 'Each quality tier represents a significant jump in assessed value. An "Excellent" rating vs "Good" can mean thousands more in taxes.'
  },
  CONDITION_MISCLASSIFICATION: {
    title: 'Condition Misclassification',
    shortTitle: 'Condition Misclassification',
    description: 'Condition ratings reflect the current state of your property. If your home has deferred maintenance issues, it should not be rated as "Good" or "Excellent".',
    whyItMatters: 'A home with a failing roof, foundation issues, or aging systems is worth less than one in perfect condition - your rating should reflect this.'
  },
  OUTDATED_SALES: {
    title: 'Inaccurate Sales Data',
    shortTitle: 'Inaccurate Sales Data',
    description: 'The county should use recent, relevant sales from your neighborhood to value your property. Old or inappropriate comparables can inflate your value.',
    whyItMatters: 'If the comparable sales are outdated, from different neighborhoods, or don\'t reflect current market conditions, your valuation could be artificially high.'
  }
};

const FLAW_ORDER: FlawType[] = ['WRONG_DETAILS', 'QUALITY_MISCLASSIFICATION', 'CONDITION_MISCLASSIFICATION', 'OUTDATED_SALES'];

// Module ID for Step 3
const STEP3_MODULE_ID = 'module-03';

// Question labels for maintenance checklist items (from ChecklistSummary)
const MAINTENANCE_QUESTION_LABELS: Record<string, Record<string, string>> = {
  ROOF: {
    age_over_15: 'Roof over 15 years old',
    visible_damage: 'Visible damaged, curling, or missing shingles',
    hail_damage: 'Evidence of hail damage',
    leaks_stains: 'Water stains on ceilings or walls',
    granule_loss: 'Granules in gutters or downspouts',
    sagging_areas: 'Visible sagging in roof deck',
    flashing_damage: 'Damaged flashing around vents/chimneys',
    attic_ventilation: 'Inadequate attic ventilation',
    wind_damage: 'Evidence of wind damage from storms',
    professional_opinion: 'Professional recommended repairs',
  },
  FOUNDATION: {
    visible_cracks: 'Visible cracks in foundation or slab',
    stair_step_cracks: 'Stair-step cracks in brick veneer',
    horizontal_cracks: 'Horizontal cracks in foundation',
    doors_windows_stick: 'Doors or windows stick or won\'t close',
    uneven_floors: 'Uneven or sloping floors',
    gaps_walls_ceiling: 'Gaps between walls and ceiling/floor',
    soil_separation: 'Visible separation between soil and foundation',
    pier_beam_issues: 'Sagging floors or damaged piers (Pier & Beam)',
    previous_repairs: 'Signs of previous foundation repairs',
    professional_inspection: 'Structural engineer recommended repairs',
  },
  HVAC: {
    age_over_10: 'HVAC system over 10 years old',
    uneven_cooling: 'Rooms don\'t cool properly in summer',
    high_utility_bills: 'Significantly increased electric bills',
    struggles_over_100: 'AC struggles when temperature exceeds 100°F',
    strange_noises: 'System makes unusual noises',
    poor_airflow: 'Weak airflow from vents',
    frequent_cycling: 'System turns on/off frequently',
    humidity_issues: 'Humidity problems in home',
    ductwork_issues: 'Damaged or disconnected attic ductwork',
    professional_opinion: 'HVAC professional recommended repairs',
  },
  PLUMBING: {
    pipe_age: 'Pipes original or over 30 years old',
    low_water_pressure: 'Low water pressure throughout home',
    slow_drains: 'Slow drains in multiple fixtures',
    water_discoloration: 'Discolored water from faucets',
    visible_leaks: 'Visible leaks under sinks or around fixtures',
    water_heater_issues: 'Water heater over 8-10 years old or has issues',
    slab_leak: 'Evidence of slab leak',
    sewer_issues: 'Sewer odors or backup issues',
    polybutylene_pipes: 'Polybutylene (gray) pipes present',
    freeze_damage: 'Pipe damage from freeze event',
    professional_opinion: 'Plumber recommended repairs or repiping',
  },
  ELECTRICAL: {
    panel_age: 'Electrical panel over 25 years old',
    fuse_box: 'Fuse box instead of circuit breakers',
    panel_brand: 'Federal Pacific, Zinsco, or Pushmatic panel',
    frequent_trips: 'Circuit breakers trip frequently',
    flickering_lights: 'Flickering or dimming lights when AC runs',
    two_prong_outlets: 'Ungrounded (2-prong) outlets present',
    warm_outlets: 'Outlets or switches feel warm',
    aluminum_wiring: 'Aluminum wiring present',
    insufficient_capacity: 'Undersized service (100 amp or less)',
    professional_opinion: 'Electrician recommended panel upgrade or rewiring',
  },
};

// Category mapping from maintenance to condition flaw categories
const MAINTENANCE_TO_CONDITION_CATEGORY: Record<string, string> = {
  'ROOF': 'Roof',
  'FOUNDATION': 'Foundation',
  'HVAC': 'HVAC',
  'PLUMBING': 'Plumbing',
  'ELECTRICAL': 'Electrical'
};

/**
 * Transform maintenance findings from Step 2 into MisclassificationItems for Condition flaw
 * Creates pre-populated items with descriptions based on what users checked in Step 2
 */
const transformMaintenanceToConditionItems = (
  findings: any[],
  followUpConfigs?: Record<string, Record<string, any>>
): MisclassificationItem[] => {
  const items: MisclassificationItem[] = [];
  
  findings.forEach(finding => {
    const category = MAINTENANCE_TO_CONDITION_CATEGORY[finding.category];
    if (!category) return;
    
    const questionLabels = MAINTENANCE_QUESTION_LABELS[finding.category] || {};
    const checklistData = finding.checklistData || {};
    const followUpData = finding.followUpData || {};
    
    // Create items from checked maintenance issues
    Object.entries(checklistData).forEach(([key, value]) => {
      if (value !== true) return;
      
      // Get the base description from question labels
      let description = questionLabels[key] || key.replace(/_/g, ' ');
      
      // Enhance description with follow-up data if available
      const itemFollowUp = followUpData[key];
      if (itemFollowUp) {
        const details: string[] = [];
        
        // Add specific details from follow-up data
        if (itemFollowUp.roofAgeYears) details.push(`${itemFollowUp.roofAgeYears} years old`);
        if (itemFollowUp.systemAgeYears) details.push(`${itemFollowUp.systemAgeYears} years old`);
        if (itemFollowUp.waterHeaterAgeYears) details.push(`${itemFollowUp.waterHeaterAgeYears} years old`);
        if (itemFollowUp.estimatedPipeAge) details.push(`~${itemFollowUp.estimatedPipeAge} years old`);
        if (itemFollowUp.materialType) details.push(itemFollowUp.materialType);
        if (itemFollowUp.crackWidthInches) details.push(`${itemFollowUp.crackWidthInches}" wide crack`);
        if (itemFollowUp.settlementSeverity) details.push(itemFollowUp.settlementSeverity);
        if (itemFollowUp.damageDescription) details.push(itemFollowUp.damageDescription);
        if (itemFollowUp.affectedArea) details.push(itemFollowUp.affectedArea);
        
        if (details.length > 0) {
          description = `${description} (${details.join(', ')})`;
        }
      }
      
      items.push({
        id: `maintenance-${finding.category}-${key}`,
        category,
        description
      });
    });
  });
  
  return items;
};

const Step3FlawsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [property, setProperty] = useState<any>(null);
  
  // Content state
  const [videoContent, setVideoContent] = useState<VideoContent[]>([]);
  const [flawDefinitions, setFlawDefinitions] = useState<Record<string, FlawDefinition>>({});
  const [classificationResources, setClassificationResources] = useState<ClassificationResource[]>([]);
  const [activeVideo, setActiveVideo] = useState<VideoContent | null>(null);
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  
  // Maintenance findings from Step 2 (for auto-populating Condition flaw)
  const [maintenanceFindings, setMaintenanceFindings] = useState<any[]>([]);

  // Note: Evidence packet gate dialog is now handled on StepsDashboardPage
  
  // Flaw findings state
  const [flawFindings, setFlawFindings] = useState<Record<FlawType, FlawFinding | null>>({
    WRONG_DETAILS: null,
    QUALITY_MISCLASSIFICATION: null,
    CONDITION_MISCLASSIFICATION: null,
    OUTDATED_SALES: null
  });
  const [expandedFlaw, setExpandedFlaw] = useState<FlawType | null>(null);
  const [savingFlaw, setSavingFlaw] = useState<FlawType | null>(null);
  
  // Active flaw for inline checklist (similar to Step 2's activeCategory)
  const [activeFlaw, setActiveFlaw] = useState<FlawType | null>(null);
  
  // Collapsible state for video sections
  const [isIntroExpanded, setIsIntroExpanded] = useState(true);
  const [isFlawsExpanded, setIsFlawsExpanded] = useState(true);
  
  // Save status for progress persistence
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'error' | null>(null);

  // Compute pre-populated items from maintenance data for Condition flaw
  const maintenanceConditionItems = useMemo(() => {
    return transformMaintenanceToConditionItems(maintenanceFindings);
  }, [maintenanceFindings]);

  // Compute merged initial data for Condition flaw (pre-populated + saved items)
  const conditionFlawMergedData = useMemo(() => {
    const existingFinding = flawFindings.CONDITION_MISCLASSIFICATION;
    const existingData = existingFinding?.checklistData as Record<string, any> || {};
    
    // Get existing items (user-saved items)
    const existingItems: MisclassificationItem[] = Array.isArray(existingData.items) 
      ? existingData.items 
      : [];
    
    // Merge: start with existing items, then add maintenance items that don't already exist
    // An item is "new from maintenance" if its ID isn't already in the existing items
    const existingItemIds = new Set(existingItems.map(item => item.id));
    const newMaintenanceItems = maintenanceConditionItems.filter(
      item => !existingItemIds.has(item.id)
    );
    
    // Final merged items: existing first, then new maintenance items
    const mergedItems = [...existingItems, ...newMaintenanceItems];
    
    return {
      // Data to pass to the component
      initialData: {
        countyRating: existingData.countyRating || '',
        items: mergedItems,
        misclassifications: existingData.misclassifications || ''
      },
      // Track count of pre-populated items for UI feedback
      prePopulatedCount: newMaintenanceItems.length
    };
  }, [flawFindings.CONDITION_MISCLASSIFICATION, maintenanceConditionItems]);

  // Load Montserrat font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Fetch content and user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // PREREQUISITE GATE: Check if user has filed their appeal (protest filed)
        // Step 3 (Find County Flaws) should only be accessible after appeal is filed
        try {
          const progressRes = await apiClient.courses.getModule('module-01', {});
          if (progressRes.data.success) {
            const moduleProgress = progressRes.data.data.progress || 0;
            if (moduleProgress < 100) {
              console.log('⚠️ Appeal not filed - redirecting to dashboard');
              navigate('/dashboard', { replace: true });
              return;
            }
          }
        } catch (gateErr) {
          console.error('Failed to check appeal status:', gateErr);
          // On error, allow access (don't block the user) - dashboard will gate if needed
        }

        // Fetch flaw checklist definitions (public endpoint)
        const definitionsRes = await apiClient.flaws.getDefinitions();
        if (definitionsRes.data.success) {
          setFlawDefinitions(definitionsRes.data.data);
        }

        // Fetch module content for Step 3
        const moduleRes = await apiClient.courses.getModule('module-03', {});
        const videos: VideoContent[] = [];

        if (moduleRes.data.success && moduleRes.data.data.content) {
          moduleRes.data.data.content.forEach((content: any) => {
            const metadata = content.metadata || {};
            let category: VideoContent['category'] = 'intro';
            
            if (metadata.flawType) {
              category = 'flaw';
            } else if (metadata.sectionGroup === 'outro') {
              category = 'outro';
            }

            videos.push({
              id: content.id,
              title: metadata.title || 'Video',
              description: metadata.description || '',
              duration: formatDuration(metadata.duration || 0),
              durationSeconds: metadata.duration || 0,
              videoUrl: content.contentUrl || '',
              category,
              flawType: metadata.flawType,
              flawNumber: metadata.flawNumber,
              isDescriptionVideo: metadata.isDescriptionVideo || false
            });
          });
        }

        setVideoContent(videos);

        // Auto-select first intro video
        const firstIntro = videos.find(v => v.category === 'intro');
        if (firstIntro) {
          setActiveVideo(firstIntro);
        }

        // Fetch user's property
        const profileRes = await apiClient.users.getProfile();
        const profile = profileRes.data.data.profile || profileRes.data.data;
        if (profile.properties?.length > 0) {
          const userProperty = profile.properties[0];
          setProperty(userProperty);

          // Fetch existing flaw findings
          try {
            const findingsRes = await apiClient.flaws.getPropertyFindings(userProperty.id);
            if (findingsRes.data.success && findingsRes.data.data) {
              const findings: Record<FlawType, FlawFinding | null> = {
                WRONG_DETAILS: null,
                QUALITY_MISCLASSIFICATION: null,
                CONDITION_MISCLASSIFICATION: null,
                OUTDATED_SALES: null
              };
              
              findingsRes.data.data.forEach((f: FlawFinding) => {
                findings[f.flawType] = f;
              });
              
              setFlawFindings(findings);
            }
          } catch {
            // No findings yet - that's fine
          }

          // Fetch maintenance data from Step 2 for auto-populating Condition flaw
          try {
            const maintenanceRes = await apiClient.maintenance.getReportByProperty(userProperty.id);
            if (maintenanceRes.data.success && maintenanceRes.data.data?.findings) {
              setMaintenanceFindings(maintenanceRes.data.data.findings);
            }
          } catch {
            // No maintenance data yet - that's fine
          }

          // Fetch classification guide PDFs (quality and condition guides)
          try {
            const guidesRes = await apiClient.flaws.getClassificationGuides(userProperty.id);
            if (guidesRes.data.success && guidesRes.data.data) {
              // Combine quality and condition guides into a single array for the component
              const allGuides = [
                ...guidesRes.data.data.quality,
                ...guidesRes.data.data.condition
              ];
              setClassificationResources(allGuides);
            }
          } catch (err) {
            console.warn('Could not fetch classification guides:', err);
            // Non-critical - page still works without guides
          }

          // Evidence packet gate data fetch removed - gate is now handled on dashboard
        }
      } catch (err: any) {
        console.error('Error fetching Step 3 content:', err);
        setError('Failed to load course content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Evidence packet gate is now handled on StepsDashboardPage before navigation here

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoComplete = () => {
    if (activeVideo) {
      const newCompleted = new Set(completedVideos);
      newCompleted.add(activeVideo.id);
      setCompletedVideos(newCompleted);

      // Auto-advance to next video in the list
      const allVideos = [...getIntroVideos(), ...getFlawVideos()];
      const currentIndex = allVideos.findIndex(v => v.id === activeVideo.id);
      if (currentIndex < allVideos.length - 1) {
        setTimeout(() => {
          setActiveVideo(allVideos[currentIndex + 1]);
        }, 1500);
      }
    }
  };

  const handleSaveFinding = async (flawType: FlawType, data: { 
    checklistData: Record<string, boolean | string> | TextboxFlawData; 
    followUpData: Record<string, Record<string, string | number>>;
    notes: string;
  }) => {
    if (!property) return;

    setSavingFlaw(flawType);
    try {
      // Clean followUpData: strip empty string values so optional fields don't cause validation errors
      const cleanedFollowUpData: Record<string, Record<string, string | number>> = {};
      for (const [questionId, fields] of Object.entries(data.followUpData)) {
        const cleanedFields: Record<string, string | number> = {};
        for (const [key, val] of Object.entries(fields)) {
          if (val !== '' && val !== undefined && val !== null) {
            cleanedFields[key] = val;
          }
        }
        if (Object.keys(cleanedFields).length > 0) {
          cleanedFollowUpData[questionId] = cleanedFields;
        }
      }

      const res = await apiClient.flaws.saveFinding({
        propertyId: property.id,
        flawType,
        checklistData: data.checklistData,
        followUpData: cleanedFollowUpData,
        notes: data.notes
      });

      if (res.data.success) {
        // Update local state with new finding
        const newFlawFindings = {
          ...flawFindings,
          [flawType]: res.data.data
        };
        setFlawFindings(newFlawFindings);
        
        // Calculate completed flaws for progress
        const completedFlaws = FLAW_ORDER.filter(f => {
          const finding = newFlawFindings[f];
          if (!finding) return false;
          const checkedCount = Object.values(finding.checklistData as Record<string, boolean>).filter(Boolean).length;
          return checkedCount > 0 || (finding.notes !== null && finding.notes.length > 0);
        }).length;
        
        const progressPercentage = Math.round((completedFlaws / FLAW_ORDER.length) * 100);
        
        console.log(`📊 Saving Step 3 progress: ${progressPercentage}% (${completedFlaws}/${FLAW_ORDER.length} flaws)`);
        setSaveStatus('saving');
        
        try {
          await apiClient.courses.updateProgress(STEP3_MODULE_ID, {
            progressPercentage,
            timeSpent: 0
          });
          
          // Mark module complete when all 4 flaws are analyzed
          if (completedFlaws >= FLAW_ORDER.length) {
            console.log('✅ All Step 3 flaws analyzed - marking module as complete');
            await apiClient.courses.completeModule(STEP3_MODULE_ID);
          }
          
          console.log(`✅ Step 3 progress saved: ${progressPercentage}%`);
          setSaveStatus('saved');
          toast.success('Flaw analysis saved!');
          setTimeout(() => setSaveStatus(null), 2000);
          
          // Auto-advance to the next flaw after successful save
          const currentIndex = FLAW_ORDER.indexOf(flawType);
          if (currentIndex < FLAW_ORDER.length - 1) {
            // Move to the next flaw
            const nextFlaw = FLAW_ORDER[currentIndex + 1];
            setTimeout(() => {
              setActiveFlaw(nextFlaw);
              // Scroll to top of the flaw section
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 500); // Brief delay so user sees the "saved" feedback
          } else {
            // On the last flaw, clear the active flaw to show completion
            setTimeout(() => {
              setActiveFlaw(null);
            }, 500);
          }
        } catch (progressErr) {
          console.error('Failed to save Step 3 progress:', progressErr);
          setSaveStatus('error');
          setTimeout(() => setSaveStatus(null), 3000);
        }
      }
    } catch (err: any) {
      console.error('Error saving flaw finding:', err);
      const backendMessage = err?.response?.data?.error?.message;
      if (backendMessage) {
        toast.error(`Could not save: ${backendMessage}`);
      } else {
        toast.error('Something went wrong while saving your flaw analysis. Please check your connection and try again.');
      }
    } finally {
      setSavingFlaw(null);
    }
  };

  const getIntroVideos = () => videoContent.filter(v => v.category === 'intro');
  const getFlawVideos = () => videoContent.filter(v => v.category === 'flaw').sort((a, b) => (a.flawNumber || 0) - (b.flawNumber || 0));
  
  // Get description/overview video for a flaw (if exists)
  const getFlawDescriptionVideo = (flawType: FlawType) => 
    videoContent.find(v => v.flawType === flawType && v.isDescriptionVideo);

  const isFlawComplete = (flawType: FlawType): boolean => {
    const finding = flawFindings[flawType];
    if (!finding) return false;
    
    const definition = flawDefinitions[flawType];
    
    if (definition?.activityType === 'textbox') {
      // For textbox activities, check if there's meaningful content
      const countyRating = finding.checklistData['countyRating'] as string | undefined;
      const misclassifications = finding.checklistData['misclassifications'] as string | undefined;
      return !!(countyRating?.trim() || misclassifications?.trim());
    } else {
      // For checklist activities, count checked items
      const checkedCount = Object.values(finding.checklistData).filter(v => v === true).length;
      return checkedCount > 0 || (finding.notes !== null && finding.notes.length > 0);
    }
  };

  const calculateProgress = (): number => {
    const totalFlaws = FLAW_ORDER.length; // 4 flaws
    const completedFlaws = FLAW_ORDER.filter(f => isFlawComplete(f)).length;
    return Math.round((completedFlaws / totalFlaws) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading Step 3 content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Content</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Redirect to dashboard if no property - user needs to complete property onboarding first
  if (!property) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Property Required</h2>
          <p className="text-slate-600 mb-4">Please add your property details to continue with the course.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>

            {/* Progress */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-teal-600">{calculateProgress()}%</span> analyzed
              </div>
              {/* Save Status Indicator */}
              {saveStatus && (
                <div className={`text-xs font-medium px-2 py-1 rounded transition-all ${
                  saveStatus === 'saving' ? 'text-slate-500 bg-slate-100' :
                  saveStatus === 'saved' ? 'text-emerald-600 bg-emerald-50' :
                  'text-red-600 bg-red-50'
                }`}>
                  {saveStatus === 'saving' ? 'Saving...' :
                   saveStatus === 'saved' ? '✓ Saved' :
                   '✗ Save failed'}
                </div>
              )}
              <div className="w-32 h-2 bg-slate-200 rounded-full">
                <div 
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Content - Left Navigation, Right Video */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT SIDE - Navigation */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden sticky top-24">
              {/* Step Header - Primary Identity */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="pl-6">
                  <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-0.5">Step 3</p>
                  <h2 className="text-base font-bold text-slate-800 leading-tight">Find Flaws in County's Evidence</h2>
                </div>
              </div>

              {/* Introduction Videos - Collapsible */}
              <div className="border-b border-slate-100">
                {/* Collapsible Header */}
                <button
                  onClick={() => {
                    const willExpand = !isIntroExpanded;
                    setIsIntroExpanded(willExpand);
                    // Auto-collapse flaws section when expanding intro
                    if (willExpand) {
                      setIsFlawsExpanded(false);
                    }
                  }}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Introduction</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({completedVideos.size}/{getIntroVideos().length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isIntroExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isIntroExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4 space-y-2">
                    {getIntroVideos().map((video, index) => {
                      const isActive = video.id === activeVideo?.id && !activeFlaw;
                      const isCompleted = completedVideos.has(video.id);

                      return (
                        <button
                          key={video.id}
                          onClick={() => {
                            setActiveVideo(video);
                            setActiveFlaw(null); // Clear flaw when selecting intro video
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-teal-50 border-2 border-teal-300'
                              : isCompleted
                              ? 'bg-emerald-50 border-2 border-emerald-200'
                              : 'bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-emerald-200' : isActive ? 'bg-teal-200' : 'bg-slate-200'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                              ) : isActive ? (
                                <Play className="w-4 h-4 text-teal-700" />
                              ) : (
                                <span className="text-slate-600 font-bold text-sm">{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-medium text-sm leading-tight ${
                                isActive ? 'text-teal-900' : isCompleted ? 'text-emerald-900' : 'text-slate-900'
                              }`}>
                                {video.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Clock className="w-3 h-3" />
                                <span>{video.duration}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 4 Flaws Section - Collapsible */}
              <div className="bg-gradient-to-b from-teal-50/80 to-white">
                {/* Collapsible Header */}
                <button
                  onClick={() => {
                    const willExpand = !isFlawsExpanded;
                    setIsFlawsExpanded(willExpand);
                    // Auto-collapse intro videos when expanding flaws section
                    if (willExpand) {
                      setIsIntroExpanded(false);
                    }
                  }}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-teal-100/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">4 Flaws to Look For</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({FLAW_ORDER.filter(f => isFlawComplete(f)).length}/{FLAW_ORDER.length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isFlawsExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isFlawsExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-3 pb-4">
                    <p className="text-xs text-slate-500 mb-3 px-1">
                      Work through each flaw type and check off any issues you find in your county's evidence.
                    </p>
                    
                    <div className="space-y-2">
                      {FLAW_ORDER.map((flawType, index) => {
                        const isComplete = isFlawComplete(flawType);
                        const flawInfo = FLAW_DESCRIPTIONS[flawType];
                        const descriptionVideo = getFlawDescriptionVideo(flawType);
                        const isDescVideoWatched = descriptionVideo ? completedVideos.has(descriptionVideo.id) : false;
                        const isDescVideoActive = activeVideo?.id === descriptionVideo?.id && !activeFlaw;
                        const isActivityActive = activeFlaw === flawType;
                        const isThisFlawActive = isDescVideoActive || isActivityActive;
                        
                        // Calculate flaw progress: 0 = not started, 1 = video watched, 2 = activity complete
                        const flawProgress = isComplete ? 2 : isDescVideoWatched ? 1 : 0;

                        return (
                          <div
                            key={flawType}
                            className={`rounded-xl overflow-hidden transition-all duration-200 ${
                              isThisFlawActive 
                                ? 'bg-teal-50 ring-2 ring-teal-300' 
                                : isComplete 
                                ? 'bg-emerald-50/70' 
                                : 'bg-white hover:bg-slate-50'
                            } border ${isThisFlawActive ? 'border-transparent' : isComplete ? 'border-emerald-200' : 'border-slate-200'}`}
                          >
                            {/* Flaw Header - Compact */}
                            <div className="flex items-center gap-3 px-3 py-2.5">
                              {/* Progress Ring */}
                              <div className="relative w-8 h-8 flex-shrink-0">
                                <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                                  {/* Background circle */}
                                  <circle 
                                    cx="16" cy="16" r="12" 
                                    fill="none" 
                                    stroke={isComplete ? '#d1fae5' : '#e2e8f0'} 
                                    strokeWidth="3"
                                  />
                                  {/* Progress arc - first half for video */}
                                  {flawProgress >= 1 && (
                                    <circle 
                                      cx="16" cy="16" r="12" 
                                      fill="none" 
                                      stroke="#10b981" 
                                      strokeWidth="3"
                                      strokeDasharray={`${12 * Math.PI} ${12 * Math.PI}`}
                                      strokeDashoffset={12 * Math.PI}
                                      className="transition-all duration-500"
                                    />
                                  )}
                                  {/* Progress arc - second half for activity */}
                                  {flawProgress >= 2 && (
                                    <circle 
                                      cx="16" cy="16" r="12" 
                                      fill="none" 
                                      stroke="#10b981" 
                                      strokeWidth="3"
                                      strokeDasharray={`${12 * Math.PI * 2} 0`}
                                      className="transition-all duration-500"
                                    />
                                  )}
                                </svg>
                                {/* Center number or check */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  {isComplete ? (
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                  ) : (
                                    <span className={`text-xs font-bold ${isThisFlawActive ? 'text-teal-700' : 'text-slate-600'}`}>
                                      {index + 1}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Flaw Title */}
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-semibold text-sm leading-tight ${
                                  isThisFlawActive ? 'text-teal-900' : isComplete ? 'text-emerald-800' : 'text-slate-800'
                                }`}>
                                  {flawInfo.shortTitle}
                                </h4>
                                {isComplete && (
                                  <span className="text-xs text-emerald-600 font-medium">Complete</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Two-Step Actions - Compact Row */}
                            <div className="flex gap-1.5 px-3 pb-2.5">
                              {/* Step 1: Overview Video */}
                              {descriptionVideo && (
                                <button
                                  onClick={() => {
                                    setActiveVideo(descriptionVideo);
                                    setActiveFlaw(null);
                                    setIsIntroExpanded(false);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  className={`flex-1 flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-all ${
                                    isDescVideoActive
                                      ? 'bg-teal-100 text-teal-800 ring-1 ring-teal-300'
                                      : isDescVideoWatched
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                    isDescVideoWatched ? 'bg-emerald-200' : isDescVideoActive ? 'bg-teal-200' : 'bg-slate-200'
                                  }`}>
                                    {isDescVideoWatched ? (
                                      <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                                    ) : (
                                      <Play className="w-3 h-3" />
                                    )}
                                  </div>
                                  <span className="font-medium truncate">Overview</span>
                                  <span className="text-[10px] opacity-70 ml-auto">{descriptionVideo.duration}</span>
                                </button>
                              )}
                              
                              {/* Step 2: Activity */}
                              <button
                                onClick={() => {
                                  setActiveFlaw(flawType);
                                  setActiveVideo(null);
                                  setIsIntroExpanded(false);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className={`flex-1 flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-all ${
                                  isActivityActive
                                    ? 'bg-teal-100 text-teal-800 ring-1 ring-teal-300'
                                    : isComplete
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                              >
                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                  isComplete ? 'bg-emerald-200' : isActivityActive ? 'bg-teal-200' : 'bg-slate-200'
                                }`}>
                                  {isComplete ? (
                                    <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                                  ) : (
                                    <ClipboardList className="w-3 h-3" />
                                  )}
                                </div>
                                <span className="font-medium truncate">Activity</span>
                                {isComplete && (
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600 ml-auto flex-shrink-0" />
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Continue to Step 4 Button */}
                    {calculateProgress() >= 60 && (
                      <button
                        onClick={() => navigate('/step/4/arguments')}
                        className="w-full mt-3 py-2.5 px-4 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                      >
                        Continue to Step 4
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Video Player OR Active Flaw Checklist */}
          <div className="lg:col-span-8 order-1 lg:order-2 space-y-6">
            {/* Show Flaw Activity when a flaw is active */}
            {activeFlaw && flawDefinitions[activeFlaw] ? (
              <FlawSection
                key={activeFlaw}
                flawType={activeFlaw}
                flawNumber={FLAW_ORDER.indexOf(activeFlaw) + 1}
                title={FLAW_DESCRIPTIONS[activeFlaw].title}
                shortTitle={FLAW_DESCRIPTIONS[activeFlaw].shortTitle}
                description={FLAW_DESCRIPTIONS[activeFlaw].description}
                whyItMatters={FLAW_DESCRIPTIONS[activeFlaw].whyItMatters}
                checklistItems={flawDefinitions[activeFlaw].items || []}
                initialChecklistData={
                  activeFlaw === 'CONDITION_MISCLASSIFICATION' 
                    ? conditionFlawMergedData.initialData as unknown as Record<string, boolean | string>
                    : flawFindings[activeFlaw]?.checklistData || {}
                }
                initialFollowUpData={(flawFindings[activeFlaw]?.followUpData as Record<string, Record<string, string | number>>) || {}}
                initialNotes={flawFindings[activeFlaw]?.notes || ''}
                onSave={(data) => handleSaveFinding(activeFlaw, data)}
                isExpanded={true}
                onToggle={() => {}}
                isSaving={savingFlaw === activeFlaw}
                hideVideo={true}
                activityType={flawDefinitions[activeFlaw].activityType || 'checklist'}
                textboxConfig={flawDefinitions[activeFlaw].textboxConfig}
                classificationResources={(() => {
                  // Merge S3-fetched resources with definition fallback resources
                  const s3Guides = classificationResources.filter(r => r.flawType === activeFlaw);
                  // If S3 guides are available, use those (they have presigned URLs)
                  if (s3Guides.length > 0) return s3Guides;
                  // Fallback: use definition resources so guides always show
                  return (flawDefinitions[activeFlaw]?.resources || []).map(r => ({
                    ...r,
                    flawType: activeFlaw
                  }));
                })()}
                prePopulatedItemCount={
                  activeFlaw === 'CONDITION_MISCLASSIFICATION' 
                    ? conditionFlawMergedData.prePopulatedCount 
                    : 0
                }
              />
            ) : (
              /* Show Video Player when no flaw is active */
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                {activeVideo ? (
                  <>
                    <VideoPlayer
                      key={activeVideo.id}
                      videoUrl={activeVideo.videoUrl}
                      onVideoComplete={handleVideoComplete}
                      currentProgress={0}
                      onProgress={() => {}}
                    />
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">
                            {activeVideo.title}
                          </h3>
                          <p className="text-slate-600">{activeVideo.description}</p>
                        </div>
                        {completedVideos.has(activeVideo.id) && (
                          <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                            <CheckCircle2 className="w-4 h-4" />
                            Watched
                          </div>
                        )}
                      </div>
                      
                      {/* Classification Guides & CTA for Quality/Condition flaw overviews */}
                      {activeVideo.isDescriptionVideo && 
                       (activeVideo.flawType === 'QUALITY_MISCLASSIFICATION' || activeVideo.flawType === 'CONDITION_MISCLASSIFICATION') && (
                        <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                          {/* Reference Guides - merge S3 guides with definition fallback */}
                          {(() => {
                            const s3Guides = classificationResources.filter(r => r.flawType === activeVideo.flawType);
                            const videoGuides = s3Guides.length > 0 
                              ? s3Guides 
                              : (flawDefinitions[activeVideo.flawType!]?.resources || []).map(r => ({
                                  ...r,
                                  flawType: activeVideo.flawType!
                                }));
                            return videoGuides.length > 0 ? (
                            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50/80 to-white p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center">
                                  <BookOpen className="w-4 h-4 text-teal-600" />
                                </div>
                                <div>
                                  <span className="text-sm font-semibold text-gray-800">Reference Guides</span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mb-3">
                                Download these guides to understand how {activeVideo.flawType === 'QUALITY_MISCLASSIFICATION' ? 'quality' : 'condition'} ratings work and what to look for.
                              </p>
                              <div className="space-y-2">
                                {videoGuides
                                  .sort((a, b) => (b.countySpecific ? 1 : 0) - (a.countySpecific ? 1 : 0))
                                  .map((resource) => (
                                    <a
                                      key={resource.id}
                                      href={resource.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg border text-left transition-all group ${
                                        resource.countySpecific 
                                          ? 'border-teal-200 bg-teal-50 hover:bg-teal-100 hover:border-teal-300' 
                                          : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3 min-w-0">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                          resource.countySpecific ? 'bg-teal-200/70' : 'bg-gray-100'
                                        }`}>
                                          <BookOpen className={`w-4 h-4 ${resource.countySpecific ? 'text-teal-700' : 'text-gray-500'}`} />
                                        </div>
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className={`text-sm font-medium truncate ${resource.countySpecific ? 'text-gray-800' : 'text-gray-700'}`}>
                                              {resource.title}
                                            </span>
                                            {resource.countySpecific && (
                                              <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-teal-200 text-teal-800 rounded flex-shrink-0">
                                                Your County
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${
                                        resource.countySpecific ? 'text-teal-500' : 'text-gray-400'
                                      }`} />
                                    </a>
                                  ))}
                              </div>
                            </div>
                            ) : null;
                          })()}
                          
                          {/* CTA to Activity */}
                          <button
                            onClick={() => {
                              setActiveFlaw(activeVideo.flawType!);
                              setActiveVideo(null);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
                          >
                            <ClipboardList className="w-5 h-5" />
                            Start {activeVideo.flawType === 'QUALITY_MISCLASSIFICATION' ? 'Quality' : 'Condition'} Activity
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : calculateProgress() >= 100 ? (
                  /* All flaws complete - show congratulations state */
                  <div className="aspect-video bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">All Flaws Analyzed!</h3>
                      <p className="text-slate-600 mb-6">
                        Great work! You've completed your analysis of all 4 flaw types. Your findings have been saved and will be used to build your personalized appeal arguments in Step 4.
                      </p>
                      <button
                        onClick={() => navigate('/step/4/arguments')}
                        className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                      >
                        Continue to Step 4
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 flex items-center justify-center">
                    <p className="text-slate-500">Select a video to start learning</p>
                  </div>
                )}
              </div>
            )}

            {/* How This Works Card - Show for intro videos when no flaw is active */}
            {activeVideo?.category === 'intro' && !activeFlaw && (
              <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl border border-teal-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileSearch className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 mb-2">How This Works</h2>
                    <p className="text-slate-700 mb-3">
                      First, watch the intro videos to understand how counties value properties and what to look for in your evidence packet.
                      Then, work through each of the 4 flaw types and check off any issues you find.
                    </p>
                    <p className="text-slate-600 text-sm">
                      Your findings will be saved and used to generate your personalized talking points for the assessor meeting.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* What's Next Section */}
            {calculateProgress() >= 60 && (
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Info className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-slate-900 mb-2">What's Next?</h2>
                    <p className="text-slate-700 mb-4">
                      Great progress! You've identified several flaws in the county's evidence. Continue to Step 4 to prepare your arguments 
                      and generate personalized talking points for your meeting with the assessor.
                    </p>
                    <button
                      onClick={() => navigate('/step/4/arguments')}
                      className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                      Continue to Step 4
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Navigation */}
            <div className="flex justify-between">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>

              {/* Right CTA: Next Video when viewing videos and more available, otherwise Continue to Step 4 when ready */}
              {!activeFlaw && activeVideo && (() => {
                const allVideos = [...getIntroVideos(), ...getFlawVideos()];
                const currentIndex = allVideos.findIndex(v => v.id === activeVideo.id);
                const hasNextVideo = currentIndex < allVideos.length - 1;
                
                if (hasNextVideo) {
                  return (
                    <button
                      onClick={() => {
                        setActiveVideo(allVideos[currentIndex + 1]);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                      Next Video
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  );
                } else if (calculateProgress() >= 60) {
                  return (
                    <button
                      onClick={() => navigate('/step/4/arguments')}
                      className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                      Continue to Step 4
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Evidence packet gate dialog removed - now handled on StepsDashboardPage */}
    </div>
  );
};

export default Step3FlawsPage;
